/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * @useFormState
 *
 * Simple hook for a merging strategy for any state that
 * is managing an object and does not want to necessarily
 * overwrite the entire value of the state but just augment
 * the existing object and/or replace property values.
 *
 * ```typescript
 * import {useMergeState} from '@ion/dls/src/hooks/';
 *
 * const [value, setValue] = useMergeState({
 *   name : 'Sam'
 * });
 *
 * // ...in component
 *
 * setValue({lastName: 'Arnold'}) // value === {name: 'Sam', lastName: 'Arnold'}
 *
 * ```
 *
 */
export const useMergeState = (defaultValue: GenericObject = {}): [GenericObject, Function] => {
  const [state, setNewState] = useState(defaultValue);

  const setState = (val: GenericObject) => {
    setNewState({...state, ...val});
  };

  return [state, setState];
};

export default useMergeState;
